import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { assetsApi } from '../../lib/assetsApi';
import { useNotification } from '../../contexts/NotificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiFile, FiLoader, FiCheck, FiX } = FiIcons;

export default function QuickUploader() {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const { showSuccess, showError } = useNotification();

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const result = await assetsApi.uploadAsset(file, 'quick-test');
        return {
          name: file.name,
          size: file.size,
          result,
          status: 'success'
        };
      });

      const results = await Promise.allSettled(uploadPromises);
      
      const successfulUploads = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);

      const failedUploads = results
        .filter(result => result.status === 'rejected')
        .map(result => ({
          status: 'failed',
          error: result.reason.message
        }));

      setUploadedFiles(prev => [...prev, ...successfulUploads, ...failedUploads]);

      if (successfulUploads.length > 0) {
        showSuccess(`${successfulUploads.length} file(s) uploaded successfully`);
      }

      if (failedUploads.length > 0) {
        showError(`${failedUploads.length} file(s) failed to upload`);
      }

    } catch (error) {
      showError('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const clearResults = () => {
    setUploadedFiles([]);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Quick Uploader</h1>
        <p className="text-gray-600">Quickly test file uploads to Dropbox storage</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="text-center">
          <SafeIcon icon={FiUpload} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Upload Test Files
          </h2>

          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            disabled={uploading}
            className="hidden"
            id="quick-upload"
          />
          
          <label
            htmlFor="quick-upload"
            className={`
              inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer
              ${uploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }
            `}
          >
            {uploading ? (
              <>
                <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2" />
                Choose Files
              </>
            )}
          </label>
        </div>
      </div>

      {uploadedFiles.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Upload Results ({uploadedFiles.length})
            </h3>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={clearResults}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Results
            </motion.button>
          </div>

          <div className="space-y-3">
            {uploadedFiles.map((file, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`
                  flex items-center justify-between p-4 rounded-lg border-2
                  ${file.status === 'success' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                  }
                `}
              >
                <div className="flex items-center">
                  <SafeIcon 
                    icon={file.status === 'success' ? FiCheck : FiX} 
                    className={`w-5 h-5 mr-3 ${
                      file.status === 'success' ? 'text-green-600' : 'text-red-600'
                    }`} 
                  />
                  
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    {file.size && (
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    )}
                    {file.error && (
                      <p className="text-sm text-red-600">{file.error}</p>
                    )}
                    {file.result?.dropbox_path && (
                      <p className="text-sm text-gray-500">
                        Path: {file.result.dropbox_path}
                      </p>
                    )}
                  </div>
                </div>

                <span className={`
                  px-3 py-1 rounded-full text-xs font-medium
                  ${file.status === 'success' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                  }
                `}>
                  {file.status === 'success' ? 'SUCCESS' : 'FAILED'}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}